import {
  TextField,
  Box,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  TableSortLabel,
  InputAdornment,
  Collapse,
  IconButton,
  Grid,
  TablePagination,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Input,
  Card,
} from '@mui/material'
import { formatQuantity, formatDecimalAuto } from 'shared/numberFormatter'

import React from 'react'
import { IProduct, Product, TYPE_BARANG_JADI } from 'interfaces/IProduct'
import makeStyles from '@mui/styles/makeStyles'
import API from '@aws-amplify/api'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import SearchIcon from '@mui/icons-material/Search'
import { Alert } from '@mui/material'
import { BrowserView, MobileView } from 'react-device-detect'
import { VALUE_SETENGAH_JADI } from 'enums/stockCard'
import LoadPackagingAutocomplete from 'shared/autocomplete/loadPackagingAutocomplete'
import LoadPremixAutocomplete from 'shared/autocomplete/loadPremixAutocomplete'

const useStyles = makeStyles((theme) => ({
  tinyCell: {
    width: '1%',
  },
  smallCell: {
    width: '5%',
  },
  mediumCell: {
    width: '10%',
  },
  tableSubRow: {
    backgroundColor: '#f7fbff',
  },
  selectedCard: {
    backgroundColor: 'rgba(255, 159, 165, 0.08)',
  },
}))

const ROWS_PER_PAGE = 25
let originalData: Partial<IProduct>[] | undefined = []

export const getProductFinderSelectionKey = (
  product: Partial<IProduct>,
  type?: string
) => {
  if (type !== VALUE_SETENGAH_JADI) {
    return String(product.productId ?? '')
  }

  return [
    product.productId ?? '',
    product.batchNumber ?? '',
    product.packagingId ?? '',
    product.premixId ?? '',
    product.fillingAmount ?? '',
    product.fillingUnit ?? '',
  ].join('|')
}

export default function ProductFinder(props: {
  brandId: string
  selectedProducts?: Partial<IProduct>[]
  getSelectedProducts?: (products: Partial<IProduct>[]) => void
  getAllProducts?: (products: Partial<IProduct>[]) => void
  productData?: Partial<IProduct>[]
  editableQty?: boolean
  showQty?: boolean
  hidePrice?: boolean
  showInventory?: boolean
  showDiscount?: boolean
  editableDiscount?: boolean
  editablePrice?: boolean
  isInhouse?: boolean
  showQtyEmphasis?: boolean
  hideNoQtyProducts?: boolean
  hideProductRank?: boolean
  showSelectedProducts?: boolean
  hideSearch?: boolean
  updateSelectedProducts?: boolean
  type?: string
  setUpdateSelectedProducts?: (shouldUpdate: boolean) => void
  onValidationChange?: (hasErrors: boolean) => void
  validateStock?: boolean
  // Optional external filters to pre-filter semi-finished products (OUT flow)
  packagingIdFilter?: string
  premixIdFilter?: string
  fillingUnitOverride?: string
}) {
  const { updateSelectedProducts, setUpdateSelectedProducts, type } = props
  const [sortingOrder, setSortingOrder] = React.useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = React.useState<keyof IProduct>('productId')
  const [products, setProducts] = React.useState<Partial<IProduct>[]>(
    (props.productData || []).sort((a, b) =>
      (a.rank || 0) > (b.rank || 0) ? 1 : (b.rank || 0) > (a.rank || 0) ? -1 : 0
    )
  )
  const [searchTerm, setSearchTerm] = React.useState('')
  const [searchPackagingId, setSearchPackagingId] = React.useState('')
  const [searchPremixId, setSearchPremixId] = React.useState('')
  const [isLoading, setIsLoading] = React.useState<any>(
    props.productData ? false : true
  )
  const [selectedProducts, setSelectedProducts] = React.useState<
    Partial<IProduct>[]
  >(props.selectedProducts || [])

  const [expandedRows, setExpandedRows] = React.useState<{
    [key: string]: boolean
  }>(
    products.reduce<{
      [key: string]: boolean
    }>((acc, product) => {
      acc[product.productId || ''] = true
      return acc
    }, {})
  )
  const [page, setPage] = React.useState(0)

  const classes = useStyles()
  const isSameSelectableProduct = (
    left: Partial<IProduct>,
    right: Partial<IProduct>
  ) =>
    getProductFinderSelectionKey(left, type) ===
    getProductFinderSelectionKey(right, type)

  React.useEffect(() => {
    if (updateSelectedProducts && setUpdateSelectedProducts) {
      setSelectedProducts(props.selectedProducts || [])
      setUpdateSelectedProducts(false)
    }
  }, [
    updateSelectedProducts,
    setUpdateSelectedProducts,
    props.selectedProducts,
  ])

  const loadProduct = (): void => {
    setIsLoading(true)
    const param = {
      queryStringParameters: {
        ...(props.brandId ? { brandId: props.brandId } : {}),
        type: type ?? TYPE_BARANG_JADI,
        productStatus: 'Active',
        ...(props.packagingIdFilter
          ? { packagingId: props.packagingIdFilter }
          : {}),
        ...(props.premixIdFilter ? { premixId: props.premixIdFilter } : {}),
        ...(type === VALUE_SETENGAH_JADI ? { expandBatches: 'true' } : {}),
      },
    }
    API.get('products', `/products/getProduct`, param)
      .then((response: IProduct[]) => {
        let products = response
        if (props.selectedProducts != null) {
          products = response.map((product) => {
            const matchingProduct = props.selectedProducts!.find(
              (selectedProduct) => isSameSelectableProduct(selectedProduct, product)
            )
            if (matchingProduct != null) {
              return {
                ...product,
                productQty:
                  matchingProduct.productQty || (matchingProduct as any).qty || 0,
                discount: matchingProduct.discount || 0,
                orderId: matchingProduct.orderId || 0,
                salesOrderId: matchingProduct.salesOrderId || 0,
                invoiceId: matchingProduct.invoiceId || 0,
                orderProductId: matchingProduct.orderProductId || 0,
                salesOrderProductId: matchingProduct.salesOrderProductId || 0,
                invoiceProductId: matchingProduct.invoiceProductId || 0,
              }
            } else {
              return product
            }
          })
        }
        const productIdToObject: { [key: string]: boolean } = {}
        products.forEach(
          (product) => (productIdToObject[product.productId] = true)
        )

        setProducts(
          products.map((product, index) => ({ ...product, rank: index + 1 }))
        )
      })
      .catch((error) => {
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  React.useEffect(() => {
    if (!props.productData) loadProduct()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.brandId])
  React.useEffect(() => {
    if (props.getSelectedProducts) props.getSelectedProducts(selectedProducts)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProducts])
  React.useEffect(() => {
    if (props.getAllProducts) props.getAllProducts(products)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products])
  React.useEffect(() => {
    originalData = props.productData
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Check for validation errors
  React.useEffect(() => {
    if (props.onValidationChange && props.showInventory) {
      const validateStock = props.validateStock !== false
      const hasErrors =
        validateStock &&
        products.some(
          (product) =>
            product.productQty !== null &&
            product.productQty !== undefined &&
            Number(product.productQty) > Number(product.inventory || 0)
        )
      props.onValidationChange(hasErrors)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, props.validateStock])

  // Sync external filters into local search state when provided
  React.useEffect(() => {
    if (props.packagingIdFilter !== undefined) {
      setSearchPackagingId(props.packagingIdFilter || '')
      setPage(0)
    }
  }, [props.packagingIdFilter])

  React.useEffect(() => {
    if (props.premixIdFilter !== undefined) {
      setSearchPremixId(props.premixIdFilter || '')
      setPage(0)
    }
  }, [props.premixIdFilter])

  // When both filters are set (OUT flow for semi-finished), refresh product list
  React.useEffect(() => {
    const hasFilters =
      (props.premixIdFilter || '').length > 0 ||
      (props.packagingIdFilter || '').length > 0
    if (hasFilters && props.type === VALUE_SETENGAH_JADI) {
      loadProduct()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.packagingIdFilter, props.premixIdFilter])

  const getFilteredProducts = () => {
    if (props.type === VALUE_SETENGAH_JADI) {
      return products.filter(
        (product) =>
          (product.premixId ?? '')
            .toUpperCase()
            .includes(searchPremixId.toUpperCase()) &&
          (product.packagingId ?? '')
            .toUpperCase()
            .includes(searchPackagingId.toUpperCase())
      )
    } else {
      return products.filter((product) =>
        (product.productName || '')
          .toUpperCase()
          .includes(searchTerm.toUpperCase())
      )
    }
  }

  function stableSort(
    array: Partial<IProduct>[],
    comparator: (a: IProduct, b: IProduct) => number
  ) {
    const stabilizedThis = array.map(
      (el, index) => [el, index] as [IProduct, number]
    )
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0])
      if (order !== 0) return order
      return a[1] - b[1]
    })
    return stabilizedThis.map((el) => el[0])
  }

  function getComparator<Key extends keyof any>(
    order: 'asc' | 'desc',
    orderBy: Key
  ): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy)
  }

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1
    }
    if (b[orderBy] > a[orderBy]) {
      return 1
    }
    return 0
  }

  const headCells = () => {
    if (type === VALUE_SETENGAH_JADI) {
      return [
        {
          id: 'productQty',
          numeric: false,
          disablePadding: false,
          label: 'Qty',
          width: 'small',
        },
        {
          id: 'batchNumber',
          numeric: false,
          disablePadding: false,
          label: 'No. Bets',
          width: 'small',
        },
        {
          id: 'packagingId',
          numeric: false,
          disablePadding: false,
          label: 'Kode Kemasan',
          width: 'small',
        },
        {
          id: 'fillingAmount',
          numeric: false,
          disablePadding: false,
          label: 'Filling Amount',
          width: 'small',
        },
        {
          id: 'inventory',
          numeric: false,
          disablePadding: false,
          label: 'Stock',
          width: 'small',
        },
      ]
    }
    let headers = []
    if (props.showQty) {
      headers.push({
        id: 'productQty',
        numeric: false,
        disablePadding: false,
        label: 'Qty',
        width: 'small',
      })
    }

    if (props.showInventory) {
      headers.push({
        id: 'inventory',
        numeric: false,
        disablePadding: false,
        label: 'Stok',
        width: 'small',
      })
    }

    headers.push(
      ...[
        {
          id: 'productName',
          numeric: false,
          disablePadding: false,
          label: 'Nama',
          width: 'medium',
        },
        {
          id: 'packagingId',
          numeric: false,
          disablePadding: false,
          label: 'Kemasan',
          width: 'medium',
        },
        {
          id: 'weight',
          numeric: false,
          disablePadding: false,
          label: 'Berat',
          width: 'small',
        },
        {
          id: 'fillingAmount',
          numeric: false,
          disablePadding: false,
          label: 'Filling',
          width: 'small',
        },
      ]
    )

    if (!props.hidePrice)
      headers.push({
        id: 'unitPrice',
        numeric: false,
        disablePadding: false,
        label: 'Harga',
        width: props.editablePrice ? 'medium' : 'small',
      })

    if (props.showDiscount) {
      headers.push({
        id: 'discount',
        numeric: false,
        disablePadding: false,
        label: 'Diskon',
        width: 'small',
      })
    }

    headers = headers.concat([
      {
        id: 'halal',
        numeric: false,
        disablePadding: false,
        label: 'Halal',
        width: 'tiny',
      },
      {
        id: 'box',
        numeric: false,
        disablePadding: false,
        label: 'Dus',
        width: 'tiny',
      },
      {
        id: 'wrap',
        numeric: false,
        disablePadding: false,
        label: 'Shrink Wrap',
        width: 'tiny',
      },
      {
        id: 'seal',
        numeric: false,
        disablePadding: false,
        label: 'Segel',
        width: 'tiny',
      },
    ])

    return headers
  }

  const selectedHeadCells = () => {
    if (type === VALUE_SETENGAH_JADI) {
      return headCells()
    }
    if (!props.hideProductRank) {
      return [
        ...headCells().slice(0, 2),
        {
          id: 'rank',
          numeric: false,
          disablePadding: false,
          label: 'Urutan',
          width: 'tiny',
        },
        ...headCells().slice(2),
      ]
    }
    return headCells()
  }

  const createSortHandler =
    (property: keyof IProduct) => (event: React.MouseEvent<unknown>) => {
      const isAsc = orderBy === property && sortingOrder === 'asc'
      setSortingOrder(isAsc ? 'desc' : 'asc')
      setOrderBy(property)
    }

  const handleDiscountChange = (
    productId: number,
    discountInput?: string,
    discountType?: string
  ) => {
    const matchingProduct =
      products.find((product) => product.productId === productId) ||
      new Product()

    let discount = discountInput ? Number(discountInput) : 0

    if (discount < 0) {
      return
    }

    let finalDiscount = 0
    if (matchingProduct.discountType === 'percent') {
      discount = Math.min(discount, 100)
      finalDiscount =
        (Number(matchingProduct.unitPrice || 0) *
          Number(matchingProduct.productQty || 0) *
          discount) /
        100
    } else if (matchingProduct.discountType === 'grosir') {
      discount = Math.min(discount, Number(matchingProduct.unitPrice || 0))
      finalDiscount =
        (Number(matchingProduct.unitPrice || 0) - discount) *
        Number(matchingProduct.productQty || 0)
    } else if (matchingProduct.discountType === 'pcs') {
      discount = Math.min(discount, Number(matchingProduct.productQty))
      finalDiscount = Number(matchingProduct.unitPrice || 0) * discount
    } else {
      finalDiscount = discount
    }

    const finalProduct = {
      ...matchingProduct,
      discount: finalDiscount,
      displayDiscount: discount,
      ...(discountType != null && { discountType }),
    }

    setProducts((prevState) =>
      prevState.map((prevProduct) => {
        if (prevProduct.productId === productId) {
          return finalProduct
        }
        return prevProduct
      })
    )

    setSelectedProducts((prevState) =>
      prevState.map((prevProduct) => {
        if (prevProduct.productId === productId) {
          return finalProduct
        }
        return prevProduct
      })
    )
  }

  const handleQtyChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    product: Partial<IProduct>
  ) => {
    const rawValue = e.target.value
    let productQty: number | null = null
    if (rawValue === '') {
      productQty = null
    } else {
      const parsed = Number(rawValue)
      if (Number.isFinite(parsed) && parsed >= 0) {
        productQty = parsed
      } else {
        productQty = null
      }
    }

    // make sure we can't go above the ordered amount
    const originalProduct = originalData?.find(
      (originalProduct) => isSameSelectableProduct(originalProduct, product)
    )

    if (
      !props.isInhouse &&
      props.validateStock !== false &&
      originalProduct &&
      productQty !== null &&
      Number(productQty) > (originalProduct.productQty || 0)
    ) {
      setProducts((prevState) =>
        prevState.map((prevProduct) =>
          isSameSelectableProduct(prevProduct, product)
            ? {
                ...prevProduct,
                productQty: originalProduct.productQty || (null as any),
                discount: 0,
                displayDiscount: 0,
              }
            : prevProduct
        )
      )
    } else {
      setProducts((prevState) =>
        prevState.map((prevProduct) =>
          isSameSelectableProduct(prevProduct, product)
            ? {
                ...prevProduct,
                productQty: rawValue as any,
                discount: 0,
                displayDiscount: 0,
              }
            : prevProduct
        )
      )
    }

    const isSelected = isProductSelected(product)
    // remove product from list of selected products if qty is 0 or null
    if (!productQty) {
      setSelectedProducts((prevState) => {
        return prevState.filter(
          (selectedProduct) => !isSameSelectableProduct(selectedProduct, product)
        )
      })
    } else {
      setSelectedProducts((prevState) => {
        const notSelectedAndNotEmpty = Number(productQty) > 0 && !isSelected
        if (notSelectedAndNotEmpty) {
          const matchingProduct =
            products.find((candidate) =>
              isSameSelectableProduct(candidate, product)
            ) ||
            new Product()
          return [...prevState, { ...matchingProduct, productQty }].map(
            (product, idx) => ({ ...product, rank: idx + 1 })
          )
        } else {
          return prevState.map((prevProduct) =>
            isSameSelectableProduct(prevProduct, product)
              ? {
                  ...prevProduct,
                  productQty,
                  discount: 0,
                }
              : prevProduct
          )
        }
      })
    }
  }

  const isProductSelected = (product: Partial<IProduct>) => {
    return !!selectedProducts.find(
      (selectedProduct) => isSameSelectableProduct(selectedProduct, product)
    )
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

   const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setPage(0)
  }

  const handleDiscountTypeChange = (
    productId: number,
    discountType: string
  ) => {
    handleDiscountChange(productId, undefined, discountType)
  }

  const getProductsToShow = () => {
    let data = stableSort(
      getFilteredProducts(),
      getComparator(sortingOrder, orderBy)
    ).slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE)

    if (props.hideNoQtyProducts) {
      data = data.filter((product) => product.productQty)
    }

    return data
  }

  const getSelectedProductsToShow = () => {
    let data = stableSort(selectedProducts, getComparator('asc', 'rank'))
    return data
  }

  const handleRankChange = (productId: number, direction: string) => {
    const matchingProduct = selectedProducts.find(
      (product) => product.productId === productId
    )

    if (matchingProduct != null) {
      const filteredProducts = selectedProducts.filter(
        (product) => product.productId !== productId
      )

      const newRank =
        direction === 'up'
          ? (matchingProduct.rank || 0) + 1
          : Math.max((matchingProduct.rank || 0) - 1, 1)

      let rerankedProducts = [
        ...filteredProducts.slice(0, newRank - 1),
        matchingProduct,
        ...filteredProducts.slice(newRank - 1),
      ]

      rerankedProducts = rerankedProducts.map((product, index) => {
        return {
          ...product,
          rank: index + 1,
        }
      })
      setSelectedProducts(rerankedProducts)
    }
  }

  const getTotalCount = () => {
    let data = stableSort(
      getFilteredProducts(),
      getComparator(sortingOrder, orderBy)
    )

    if (props.hideNoQtyProducts) {
      data = data.filter((product) => product.productQty)
    }

    return data.length
  }

  const shouldShowExpand = () => {
    return props.editableDiscount || props.editableQty
  }

  const handlePriceChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    productId: number
  ) => {
    const unitPrice = Number(e.target.value)
    if (Number.isNaN(unitPrice) || unitPrice < 0) {
      return
    }

    setProducts((prevState) => {
      return prevState.map((product) => {
        return product.productId === productId
          ? { ...product, unitPrice }
          : product
      })
    })
    setSelectedProducts((prevState) => {
      return prevState.map((product) => {
        return product.productId === productId
          ? { ...product, unitPrice }
          : product
      })
    })
  }

  return (
    <React.Fragment>
      {props.showSelectedProducts && selectedProducts.length > 0 && (
        <Box mb={4} ml={2} mt={2}>
          <Typography variant="h6">Produk yang sudah terpilih</Typography>
          <BrowserView>
            <TableContainer style={{ backgroundColor: '#f0f7ff', borderRadius: 4 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {selectedHeadCells().map((headCell) => (
                      <TableCell
                        key={headCell.id + 'header1'}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={
                          orderBy === headCell.id ? sortingOrder : false
                        }
                        // @ts-ignore
                        className={classes[`${headCell.width}Cell`]}
                      >
                        {headCell.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSelectedProductsToShow().map((product) =>
                    type === VALUE_SETENGAH_JADI ? (
                      <TableRow key={getProductFinderSelectionKey(product, type)}>
                        <TableCell>{formatQuantity(product.productQty)}</TableCell>
                        <TableCell>{product.batchNumber || '-'}</TableCell>
                        <TableCell>{product.packagingId}</TableCell>
                        <TableCell>{formatDecimalAuto(product.fillingAmount as any)}</TableCell>
                        <TableCell>{formatQuantity(product.inventory)}</TableCell>
                      </TableRow>
                    ) : (
                      <TableRow key={product.productId}>
                        {props.showQty && (
                          <TableCell>{formatQuantity(product.productQty)}</TableCell>
                        )}
                        {props.showInventory && (
                          <TableCell>{formatQuantity(product.inventory)}</TableCell>
                        )}
                        {!props.hideProductRank && (
                          <TableCell>
                            <Grid container justifyContent="center">
                              <Grid item xs={12}>
                                <IconButton
                                  aria-label="up"
                                  onClick={() =>
                                    handleRankChange(product.productId!, 'down')
                                  }
                                  style={{
                                    paddingTop: '3px',
                                    paddingBottom: '3px',
                                  }}
                                  size="large"
                                >
                                  <KeyboardArrowUpIcon />
                                </IconButton>{' '}
                              </Grid>
                              <Grid item xs={12}>
                                <IconButton
                                  aria-label="down"
                                  onClick={() =>
                                    handleRankChange(product.productId!, 'up')
                                  }
                                  style={{
                                    paddingTop: '3px',
                                    paddingBottom: '3px',
                                  }}
                                  size="large"
                                >
                                  <KeyboardArrowDownIcon />
                                </IconButton>{' '}
                              </Grid>
                            </Grid>
                          </TableCell>
                        )}
                        <TableCell>
                          {product.productName}
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            ID: {product.productId}
                          </Typography>
                        </TableCell>
                        <TableCell>{product.packagingName}</TableCell>
                        <TableCell>{`${formatDecimalAuto(product.weight as any) || 0}kg`}</TableCell>
                        <TableCell>
                          {(product as any).fillingAmount
                            ? `${formatDecimalAuto((product as any).fillingAmount)} ${props.fillingUnitOverride || (product as any).fillingUnit || ''}`
                            : '-'}
                        </TableCell>
                        {!props.hidePrice && (
                          <TableCell>
                            {`Rp.${new Intl.NumberFormat('en-US').format(
                              Number(product.unitPrice)
                            )}`}
                          </TableCell>
                        )}
                        {props.showDiscount && (
                          <TableCell>
                            {`Rp.${new Intl.NumberFormat('en-US').format(
                              Number(product.discount || 0)
                            )}`}
                          </TableCell>
                        )}
                        <TableCell>
                          <Checkbox disabled checked={product.halal} />
                        </TableCell>
                        <TableCell>
                          <Checkbox disabled checked={product.box} />
                        </TableCell>
                        <TableCell>
                          <Checkbox disabled checked={product.wrap} />
                        </TableCell>
                        <TableCell>
                          <Checkbox disabled checked={product.seal} />
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </BrowserView>
          <MobileView>
            {getSelectedProductsToShow().map((product) => (
              <Box pb={2} pt={2} key={getProductFinderSelectionKey(product, type)}>
                <Card>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <React.Fragment>
                          <TableRow>
                            <TableCell colSpan={2}>
                              <Box fontWeight="bold" display="inline">
                                {product.productName}
                              </Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                              >
                                ID: {product.productId}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Box fontWeight="bold" display="inline">
                                Packaging
                              </Box>
                            </TableCell>
                            <TableCell>{product.packagingName}</TableCell>
                          </TableRow>
                          {props.showQty && (
                            <TableRow>
                              <TableCell>
                                <Box fontWeight="bold" display="inline">
                                  Qty
                                </Box>
                              </TableCell>
                              <TableCell>
                                {props.editableQty ? (
                                  <TextField
                                    label="Qty"
                                    type="number"
                                    value={product.productQty}
                                    onChange={(e) =>
                                      handleQtyChange(e, product)
                                    }
                                    onKeyDown={(ev) => {
                                      if (ev.key === '.' || ev.key === ',')
                                        ev.preventDefault()
                                    }}
                                    inputProps={{
                                      min: 0,
                                      step: 1,
                                    }}
                                    error={
                                      props.showInventory &&
                                      props.validateStock !== false &&
                                      product.productQty !== null &&
                                      product.productQty !== undefined &&
                                      Number(product.productQty) >
                                        Number(product.inventory || 0)
                                    }
                                    helperText={
                                      props.showInventory &&
                                      props.validateStock !== false &&
                                      product.productQty !== null &&
                                      product.productQty !== undefined &&
                                      Number(product.productQty) >
                                        Number(product.inventory || 0)
                                        ? `Melebihi stok (${formatQuantity(
                                            product.inventory
                                          )})`
                                        : ''
                                    }
                                  />
                                ) : (
                                  formatQuantity(product.productQty)
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                          {props.showInventory && (
                            <TableRow>
                              <TableCell>
                                <Box fontWeight="bold" display="inline">
                                  Stok
                                </Box>
                              </TableCell>
                              <TableCell>{formatQuantity(product.inventory)}</TableCell>
                            </TableRow>
                          )}
                          {!props.hidePrice && (
                            <TableRow>
                              <TableCell>
                                <Box fontWeight="bold" display="inline">
                                  Harga
                                </Box>
                              </TableCell>
                              <TableCell>
                                {props.editablePrice ? (
                                  <TextField
                                    value={product.unitPrice}
                                    label="Harga"
                                    type="number"
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>
                                    ) =>
                                      handlePriceChange(e, product.productId)
                                    }
                                  />
                                ) : (
                                  `Rp.${new Intl.NumberFormat('en-US').format(
                                    Number(product.unitPrice)
                                  )}`
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                          {props.showDiscount && (
                            <React.Fragment>
                              <TableRow>
                                <TableCell>
                                  <Box fontWeight="bold" display="inline">
                                    Diskon
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  {`Rp.${new Intl.NumberFormat('en-US').format(
                                    Number(product.discount || 0)
                                  )}`}
                                  {shouldShowExpand() && (
                                    <IconButton
                                      aria-label="expand row"
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setExpandedRows({
                                          ...expandedRows,
                                          [product.productId]:
                                            !expandedRows[product.productId],
                                        })
                                      }}
                                    >
                                      {expandedRows[product.productId] ? (
                                        <KeyboardArrowUpIcon />
                                      ) : (
                                        <KeyboardArrowDownIcon />
                                      )}
                                    </IconButton>
                                  )}
                                </TableCell>
                              </TableRow>
                              {shouldShowExpand() &&
                                expandedRows[product.productId] && (
                                  <TableRow
                                    className={classes.tableSubRow}
                                    hover
                                  >
                                    <TableCell colSpan={12}>
                                      <Collapse
                                        in={expandedRows[product.productId]}
                                        timeout="auto"
                                        unmountOnExit
                                      >
                                        <Grid container alignItems="flex-end">
                                          {props.editableDiscount && (
                                            <React.Fragment>
                                              {props.editableDiscount && (
                                                <Grid item xs={4}>
                                                  <FormControl>
                                                    <InputLabel id="discount-type">
                                                      Jenis Diskon
                                                    </InputLabel>
                                                    <Select
                                                      labelId="discount-type"
                                                      id="discount-type"
                                                      value={
                                                        product.discountType ||
                                                        'manual'
                                                      }
                                                      onChange={(e) =>
                                                        handleDiscountTypeChange(
                                                          product.productId,
                                                          String(e.target.value)
                                                        )
                                                      }
                                                    >
                                                      <MenuItem value={'pcs'}>
                                                        Pcs
                                                      </MenuItem>
                                                      <MenuItem
                                                        value={'manual'}
                                                      >
                                                        Manual
                                                      </MenuItem>
                                                      <MenuItem
                                                        value={'percent'}
                                                      >
                                                        Percent
                                                      </MenuItem>
                                                      <MenuItem
                                                        value={'grosir'}
                                                      >
                                                        Grosir
                                                      </MenuItem>
                                                    </Select>
                                                  </FormControl>
                                                </Grid>
                                              )}
                                              <Grid item xs={8}>
                                                <InputLabel htmlFor="Diskon">
                                                  Diskon
                                                </InputLabel>
                                                <Input
                                                  id="Diskon"
                                                  autoComplete="off"
                                                  type="number"
                                                  value={
                                                    product.displayDiscount ||
                                                    product.discount ||
                                                    ''
                                                  }
                                                  onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>
                                                  ) =>
                                                    handleDiscountChange(
                                                      product.productId,
                                                      e.target.value.trim()
                                                    )
                                                  }
                                                  placeholder="0"
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  disabled={
                                                    !props.editableDiscount
                                                  }
                                                  startAdornment={(() => {
                                                    if (
                                                      product.discountType ===
                                                        'manual' ||
                                                      !product.discountType ||
                                                      product.discountType ===
                                                        'grosir'
                                                    ) {
                                                      return (
                                                        <InputAdornment position="start">
                                                          Rp.
                                                        </InputAdornment>
                                                      )
                                                    }
                                                  })()}
                                                  endAdornment={(() => {
                                                    if (
                                                      product.discountType ===
                                                      'percent'
                                                    ) {
                                                      return (
                                                        <InputAdornment position="end">
                                                          %
                                                        </InputAdornment>
                                                      )
                                                    } else if (
                                                      product.discountType ===
                                                      'pcs'
                                                    ) {
                                                      return (
                                                        <InputAdornment position="end">
                                                          pcs
                                                        </InputAdornment>
                                                      )
                                                    } else if (
                                                      product.discountType ===
                                                      'grosir'
                                                    ) {
                                                      return (
                                                        <InputAdornment position="end">
                                                          hrg/pc
                                                        </InputAdornment>
                                                      )
                                                    }
                                                  })()}
                                                />
                                              </Grid>
                                              {(product.discountType ===
                                                'pcs' ||
                                                product.discountType ===
                                                  'percent' ||
                                                product.discountType ===
                                                  'grosir') && (
                                                <Grid item xs={12}>
                                                  <Typography variant="h6">
                                                    Rp.{' '}
                                                    {(
                                                      product.discount || '0'
                                                    ).toLocaleString('en-US')}
                                                  </Typography>
                                                </Grid>
                                              )}
                                              {(Number(product.discount) || 0) >
                                                0.3 *
                                                  Number(product.unitPrice) *
                                                  Number(
                                                    product.productQty
                                                  ) && (
                                                <Alert severity="warning">
                                                  Diskon lebih besar dari 30%
                                                </Alert>
                                              )}
                                              <Box mt={1}>
                                                <Typography variant="body2">
                                                  Stok saat ini: {formatQuantity(product.inventory)}
                                                </Typography>
                                              </Box>
                                            </React.Fragment>
                                          )}
                                        </Grid>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                )}
                            </React.Fragment>
                          )}
                          {!props.hideProductRank && (
                            <React.Fragment>
                              <TableCell>
                                <Box fontWeight="bold" display="inline">
                                  Urutan
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Grid container alignItems="center">
                                  <Grid item xs={6}>
                                    Urutan: {product.rank}
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Grid container justifyContent="center">
                                      <Grid item xs={12}>
                                        <IconButton
                                          aria-label="up"
                                          onClick={() =>
                                            handleRankChange(
                                              product.productId,
                                              'down'
                                            )
                                          }
                                          style={{
                                            paddingTop: '3px',
                                            paddingBottom: '3px',
                                          }}
                                          size="large"
                                        >
                                          <KeyboardArrowUpIcon />
                                        </IconButton>{' '}
                                      </Grid>
                                      <Grid item xs={12}>
                                        <IconButton
                                          aria-label="down"
                                          onClick={() =>
                                            handleRankChange(
                                              product.productId,
                                              'up'
                                            )
                                          }
                                          style={{
                                            paddingTop: '3px',
                                            paddingBottom: '3px',
                                          }}
                                          size="large"
                                        >
                                          <KeyboardArrowDownIcon />
                                        </IconButton>{' '}
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </TableCell>
                            </React.Fragment>
                          )}
                        </React.Fragment>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Box>
            ))}
          </MobileView>
        </Box>
      )}
      <BrowserView>
        <TableContainer>
          <Table>
            <TableHead>
              {!props.hideSearch &&
                (props.type === VALUE_SETENGAH_JADI ? (
                  <React.Fragment>
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Box ml={2}>
                          <LoadPackagingAutocomplete
                            name={searchPackagingId}
                            onChange={(e) =>
                              setSearchPackagingId(e?.packagingId ?? '')
                            }
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Box ml={2}>
                          <LoadPremixAutocomplete
                            name={searchPremixId}
                            onChange={(e) =>
                              setSearchPremixId(e?.premixId ?? '')
                            }
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ) : (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Box ml={2}>
                        <TextField
                          fullWidth
                          placeholder="Cari Produk..."
                          onChange={handleSearch}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              <TableRow>
                {headCells().map((headCell) => (
                  <TableCell
                    key={headCell.id + 'header'}
                    align={headCell.numeric ? 'right' : 'left'}
                    padding={headCell.disablePadding ? 'none' : 'normal'}
                    sortDirection={
                      orderBy === headCell.id ? sortingOrder : false
                    }
                    // @ts-ignore
                    className={classes[`${headCell.width}Cell`]}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? sortingOrder : 'asc'}
                      onClick={createSortHandler(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Box p={3}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {getProductsToShow().map((product) => {
                  const rowKey = getProductFinderSelectionKey(product, type)
                  return (
                  <React.Fragment key={rowKey}>
                    <TableRow
                      hover
                      key={rowKey}
                      selected={isProductSelected(product)}
                    >
                      {type === VALUE_SETENGAH_JADI ? (
                        <>
                          <TableCell>
                            <TextField
                              label="Qty"
                              type="number"
                              value={product.productQty}
                              onChange={(e) =>
                                handleQtyChange(e, product)
                              }
                              onKeyDown={(ev) => {
                                if (ev.key === '.' || ev.key === ',')
                                  ev.preventDefault()
                              }}
                              inputProps={{
                                min: 0,
                                step: 1,
                              }}
                              error={
                                props.showInventory &&
                                props.validateStock !== false &&
                                product.productQty !== null &&
                                product.productQty !== undefined &&
                                Number(product.productQty) >
                                  Number(product.inventory || 0)
                              }
                              helperText={
                                props.showInventory &&
                                props.validateStock !== false &&
                                product.productQty !== null &&
                                product.productQty !== undefined &&
                                Number(product.productQty) >
                                  Number(product.inventory || 0)
                                  ? `Melebihi stok (${formatQuantity(
                                      product.inventory
                                    )})`
                                  : ''
                              }
                            />
                          </TableCell>
                          <TableCell>{product.batchNumber || '-'}</TableCell>
                          <TableCell>{product.packagingId}</TableCell>
                          <TableCell>{formatDecimalAuto(product.fillingAmount as any)}</TableCell>
                          <TableCell>{formatQuantity(product.inventory)}</TableCell>
                        </>
                      ) : (
                        <>
                          {props.showQty && (
                            <TableCell>
                              {props.editableQty ? (
                                <TextField
                                  label="Qty"
                                  type="number"
                                  value={product.productQty}
                                  onChange={(e) =>
                                    handleQtyChange(e, product)
                                  }
                                  onKeyDown={(ev) => {
                                    if (ev.key === '.' || ev.key === ',')
                                      ev.preventDefault()
                                  }}
                                  inputProps={{
                                    min: 0,
                                    step: 1,
                                  }}
                                  error={
                                    props.showInventory &&
                                    props.validateStock !== false &&
                                    product.productQty !== null &&
                                    product.productQty !== undefined &&
                                    Number(product.productQty) >
                                      Number(product.inventory || 0)
                                  }
                                  helperText={
                                    props.showInventory &&
                                    props.validateStock !== false &&
                                    product.productQty !== null &&
                                    product.productQty !== undefined &&
                                    Number(product.productQty) >
                                      Number(product.inventory || 0)
                                      ? `Melebihi stok (${formatQuantity(
                                          product.inventory
                                        )})`
                                      : ''
                                  }
                                />
                              ) : (
                                formatQuantity(product.productQty)
                              )}
                            </TableCell>
                          )}

                          {props.showInventory && (
                            <TableCell>{formatQuantity(product.inventory)}</TableCell>
                          )}
                          <TableCell>
                            {product.productName}
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              ID: {product.productId}
                            </Typography>
                          </TableCell>
                          <TableCell>{product.packagingName}</TableCell>
                          <TableCell>{`${formatDecimalAuto(product.weight as any) || 0}kg`}</TableCell>
                          <TableCell>
                            {(product as any).fillingAmount
                              ? `${formatDecimalAuto((product as any).fillingAmount)} ${props.fillingUnitOverride || (product as any).fillingUnit || ''}`
                              : '-'}
                          </TableCell>
                          {!props.hidePrice && (
                            <TableCell>
                              {props.editablePrice ? (
                                <TextField
                                  value={product.unitPrice}
                                  label="Harga"
                                  type="number"
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                  ) => handlePriceChange(e, product.productId)}
                                />
                              ) : (
                                `Rp.${new Intl.NumberFormat('en-US').format(
                                  Number(product.unitPrice)
                                )}`
                              )}
                            </TableCell>
                          )}
                          {props.showDiscount && (
                            <TableCell>
                              {`Rp.${new Intl.NumberFormat('en-US').format(
                                Number(product.discount || 0)
                              )}`}
                              {shouldShowExpand() && (
                                <IconButton
                                  aria-label="expand row"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setExpandedRows({
                                      ...expandedRows,
                                      [product.productId]:
                                        !expandedRows[product.productId],
                                    })
                                  }}
                                >
                                  {expandedRows[product.productId] ? (
                                    <KeyboardArrowUpIcon />
                                  ) : (
                                    <KeyboardArrowDownIcon />
                                  )}
                                </IconButton>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            <Checkbox disabled checked={product.halal} />
                          </TableCell>
                          <TableCell>
                            <Checkbox disabled checked={product.box} />
                          </TableCell>
                          <TableCell>
                            <Checkbox disabled checked={product.wrap} />
                          </TableCell>
                          <TableCell>
                            <Checkbox disabled checked={product.seal} />
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                    {shouldShowExpand() && expandedRows[product.productId] && (
                      <TableRow className={classes.tableSubRow} hover>
                        <TableCell colSpan={12}>
                          <Collapse
                            in={expandedRows[product.productId]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Grid container alignItems="flex-end">
                              {props.editableDiscount && (
                                <React.Fragment>
                                  <Grid item xs={4} sm={5}>
                                    <Grid
                                      container
                                      alignItems="flex-end"
                                      alignContent="space-between"
                                    >
                                      {props.editableDiscount && (
                                        <Grid item>
                                          <FormControl>
                                            <InputLabel id="discount-type">
                                              Jenis Diskon
                                            </InputLabel>
                                            <Select
                                              labelId="discount-type"
                                              id="discount-type"
                                              value={
                                                product.discountType || 'manual'
                                              }
                                              onChange={(e) =>
                                                handleDiscountTypeChange(
                                                  product.productId,
                                                  String(e.target.value)
                                                )
                                              }
                                            >
                                              <MenuItem value={'pcs'}>
                                                Pcs
                                              </MenuItem>
                                              <MenuItem value={'manual'}>
                                                Manual
                                              </MenuItem>
                                              <MenuItem value={'percent'}>
                                                Percent
                                              </MenuItem>
                                              <MenuItem value={'grosir'}>
                                                Grosir
                                              </MenuItem>
                                            </Select>
                                          </FormControl>
                                        </Grid>
                                      )}
                                      <Grid item xs={5}>
                                        <InputLabel htmlFor="Diskon">
                                          Diskon
                                        </InputLabel>
                                        <Input
                                          id="Diskon"
                                          autoComplete="off"
                                          type="number"
                                          value={
                                            product.displayDiscount ||
                                            product.discount ||
                                            ''
                                          }
                                          onChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                          ) =>
                                            handleDiscountChange(
                                              product.productId,
                                              e.target.value.trim()
                                            )
                                          }
                                          placeholder="0"
                                          onClick={(e) => e.stopPropagation()}
                                          disabled={!props.editableDiscount}
                                          startAdornment={(() => {
                                            if (
                                              product.discountType ===
                                                'manual' ||
                                              !product.discountType ||
                                              product.discountType === 'grosir'
                                            ) {
                                              return (
                                                <InputAdornment position="start">
                                                  Rp.
                                                </InputAdornment>
                                              )
                                            }
                                          })()}
                                          endAdornment={(() => {
                                            if (
                                              product.discountType === 'percent'
                                            ) {
                                              return (
                                                <InputAdornment position="end">
                                                  %
                                                </InputAdornment>
                                              )
                                            } else if (
                                              product.discountType === 'pcs'
                                            ) {
                                              return (
                                                <InputAdornment position="end">
                                                  pcs
                                                </InputAdornment>
                                              )
                                            } else if (
                                              product.discountType === 'grosir'
                                            ) {
                                              return (
                                                <InputAdornment position="end">
                                                  hrg/pc
                                                </InputAdornment>
                                              )
                                            }
                                          })()}
                                        />
                                      </Grid>
                                      {(product.discountType === 'pcs' ||
                                        product.discountType === 'percent' ||
                                        product.discountType === 'grosir') && (
                                        <Grid item>
                                          <Box ml={1}>
                                            <Typography variant="h6">
                                              Rp.{' '}
                                              {(
                                                product.discount || '0'
                                              ).toLocaleString('en-US')}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )}
                                      {(Number(product.discount) || 0) >
                                        0.3 *
                                          Number(product.unitPrice) *
                                          Number(product.productQty) && (
                                        <Alert severity="warning">
                                          Diskon lebih besar dari 30%
                                        </Alert>
                                      )}
                                    </Grid>
                                  </Grid>
                                </React.Fragment>
                              )}
                            </Grid>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                  )
                })}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </BrowserView>
      <MobileView>
        <TableContainer>
          <TableRow>
            <TableCell colSpan={3}>
              <Box ml={2}>
                <TextField
                  fullWidth
                  placeholder="Cari Produk..."
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </TableCell>
          </TableRow>
          {getProductsToShow().map((product) => (
            <Box pb={2} pt={2} key={getProductFinderSelectionKey(product, type)}>
              <Card
                className={
                  isProductSelected(product)
                    ? classes.selectedCard
                    : ''
                }
              >
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <React.Fragment>
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Box fontWeight="bold" display="inline">
                              {product.productName}
                            </Box>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              ID: {product.productId}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Box fontWeight="bold" display="inline">
                              Packaging
                            </Box>
                          </TableCell>
                          <TableCell>{product.packagingName}</TableCell>
                        </TableRow>
                        {props.showQty && (
                          <TableRow>
                            <TableCell>
                              <Box fontWeight="bold" display="inline">
                                Qty
                              </Box>
                            </TableCell>
                            <TableCell>
                              {props.editableQty ? (
                                <TextField
                                  label="Qty"
                                  type="number"
                                  value={product.productQty}
                                  onChange={(e) =>
                                    handleQtyChange(e, product)
                                  }
                                  inputProps={{
                                    min: 0,
                                    step: '0.000001',
                                  }}
                                  error={
                                    props.showInventory &&
                                    props.validateStock !== false &&
                                    product.productQty !== null &&
                                    product.productQty !== undefined &&
                                    Number(product.productQty) >
                                      Number(product.inventory || 0)
                                  }
                                  helperText={
                                    props.showInventory &&
                                    props.validateStock !== false &&
                                    product.productQty !== null &&
                                    product.productQty !== undefined &&
                                    Number(product.productQty) >
                                      Number(product.inventory || 0)
                                      ? `Melebihi stok (${formatQuantity(
                                          product.inventory
                                        )})`
                                      : ''
                                  }
                                />
                              ) : (
                                formatQuantity(product.productQty)
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                        {props.showInventory && (
                          <TableRow>
                            <TableCell>
                              <Box fontWeight="bold" display="inline">
                                Stok
                              </Box>
                            </TableCell>
                            <TableCell>{formatQuantity(product.inventory)}</TableCell>
                          </TableRow>
                        )}
                        {!props.hidePrice && (
                          <TableRow>
                            <TableCell>
                              <Box fontWeight="bold" display="inline">
                                Harga
                              </Box>
                            </TableCell>
                            <TableCell>
                              {props.editablePrice ? (
                                <TextField
                                  value={product.unitPrice}
                                  label="Harga"
                                  type="number"
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                  ) => handlePriceChange(e, product.productId)}
                                />
                              ) : (
                                `Rp.${new Intl.NumberFormat('en-US').format(
                                  Number(product.unitPrice)
                                )}`
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                        {props.showDiscount && (
                          <React.Fragment>
                            <TableRow>
                              <TableCell>
                                <Box fontWeight="bold" display="inline">
                                  Diskon
                                </Box>
                              </TableCell>
                              <TableCell>
                                {`Rp.${new Intl.NumberFormat('en-US').format(
                                  Number(product.discount || 0)
                                )}`}
                                {shouldShowExpand() && (
                                  <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setExpandedRows({
                                        ...expandedRows,
                                        [product.productId]:
                                          !expandedRows[product.productId],
                                      })
                                    }}
                                  >
                                    {expandedRows[product.productId] ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <KeyboardArrowDownIcon />
                                    )}
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                            {shouldShowExpand() &&
                              expandedRows[product.productId] && (
                                <TableRow className={classes.tableSubRow} hover>
                                  <TableCell colSpan={12}>
                                    <Collapse
                                      in={expandedRows[product.productId]}
                                      timeout="auto"
                                      unmountOnExit
                                    >
                                      <Grid container alignItems="flex-end">
                                        {props.editableDiscount && (
                                          <React.Fragment>
                                            {props.editableDiscount && (
                                              <Grid item xs={4}>
                                                <FormControl>
                                                  <InputLabel id="discount-type">
                                                    Jenis Diskon
                                                  </InputLabel>
                                                  <Select
                                                    labelId="discount-type"
                                                    id="discount-type"
                                                    value={
                                                      product.discountType ||
                                                      'manual'
                                                    }
                                                    onChange={(e) =>
                                                      handleDiscountTypeChange(
                                                        product.productId,
                                                        String(e.target.value)
                                                      )
                                                    }
                                                  >
                                                    <MenuItem value={'pcs'}>
                                                      Pcs
                                                    </MenuItem>
                                                    <MenuItem value={'manual'}>
                                                      Manual
                                                    </MenuItem>
                                                    <MenuItem value={'percent'}>
                                                      Percent
                                                    </MenuItem>
                                                    <MenuItem value={'grosir'}>
                                                      Grosir
                                                    </MenuItem>
                                                  </Select>
                                                </FormControl>
                                              </Grid>
                                            )}
                                            <Grid item xs={8}>
                                              <InputLabel htmlFor="Diskon">
                                                Diskon
                                              </InputLabel>
                                              <Input
                                                id="Diskon"
                                                autoComplete="off"
                                                type="number"
                                                value={
                                                  product.displayDiscount ||
                                                  product.discount ||
                                                  ''
                                                }
                                                onChange={(
                                                  e: React.ChangeEvent<HTMLInputElement>
                                                ) =>
                                                  handleDiscountChange(
                                                    product.productId,
                                                    e.target.value.trim()
                                                  )
                                                }
                                                placeholder="0"
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                                disabled={
                                                  !props.editableDiscount
                                                }
                                                startAdornment={(() => {
                                                  if (
                                                    product.discountType ===
                                                      'manual' ||
                                                    !product.discountType ||
                                                    product.discountType ===
                                                      'grosir'
                                                  ) {
                                                    return (
                                                      <InputAdornment position="start">
                                                        Rp.
                                                      </InputAdornment>
                                                    )
                                                  }
                                                })()}
                                                endAdornment={(() => {
                                                  if (
                                                    product.discountType ===
                                                    'percent'
                                                  ) {
                                                    return (
                                                      <InputAdornment position="end">
                                                        %
                                                      </InputAdornment>
                                                    )
                                                  } else if (
                                                    product.discountType ===
                                                    'pcs'
                                                  ) {
                                                    return (
                                                      <InputAdornment position="end">
                                                        pcs
                                                      </InputAdornment>
                                                    )
                                                  } else if (
                                                    product.discountType ===
                                                    'grosir'
                                                  ) {
                                                    return (
                                                      <InputAdornment position="end">
                                                        hrg/pc
                                                      </InputAdornment>
                                                    )
                                                  }
                                                })()}
                                              />
                                            </Grid>
                                            {(product.discountType === 'pcs' ||
                                              product.discountType ===
                                                'percent' ||
                                              product.discountType ===
                                                'grosir') && (
                                              <Grid item xs={12}>
                                                <Typography variant="h6">
                                                  Rp.{' '}
                                                  {(
                                                    product.discount || '0'
                                                  ).toLocaleString('en-US')}
                                                </Typography>
                                              </Grid>
                                            )}
                                            {(Number(product.discount) || 0) >
                                              0.3 *
                                                Number(product.unitPrice) *
                                                Number(product.productQty) && (
                                              <Alert severity="warning">
                                                Diskon lebih besar dari 30%
                                              </Alert>
                                            )}
                                          </React.Fragment>
                                        )}
                                      </Grid>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              )}
                          </React.Fragment>
                        )}
                      </React.Fragment>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Box>
          ))}
        </TableContainer>
      </MobileView>
      {products.length > ROWS_PER_PAGE && (
        <TablePagination
          count={getTotalCount()}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[]}
          page={page}
          onPageChange={handleChangePage}
        />
      )}
    </React.Fragment>
  )
}
